import type { EntityAddress } from '../types';

// Shared formatting/cleaning for an entity's structured Bill To / Ship To
// address — used by the entity form, the detail modal, and (later) the
// printed invoice.

/** Non-empty parts as display lines, e.g. for a stacked address block. */
export function formatAddressLines(a?: EntityAddress | null): string[] {
  if (!a) return [];
  const cityLine = [a.city, a.state, a.postalCode].map(s => (s ?? '').trim()).filter(Boolean).join(', ');
  return [a.name, a.line1, a.line2, cityLine, a.country]
    .map(s => (s ?? '').trim())
    .filter(Boolean);
}

/** Same parts joined onto one line, for a table cell. */
export function formatAddressOneLine(a?: EntityAddress | null): string {
  return formatAddressLines(a).join(' · ');
}

/** Trim every field, drop empties. Returns null if the whole address is
 *  empty — Firestore rejects `undefined`, so callers store `null` / omit. */
export function cleanAddress(a: EntityAddress): EntityAddress | null {
  const out: EntityAddress = {};
  (Object.keys(a) as (keyof EntityAddress)[]).forEach(k => {
    const v = (a[k] ?? '').trim();
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : null;
}

/** Lift a legacy free-text `address` string into the structured shape so
 *  opening/saving an old entity upgrades it without losing anything. */
export function legacyToBillTo(addr?: string): EntityAddress {
  const v = (addr ?? '').trim();
  return v ? { line1: v } : {};
}
